/**
 * Grounded entity resolution for Ask Dewey answers.
 *
 * The agent answer is natural-language prose that *references* graph entities
 * by id (rendered as inline code, e.g. `chart-of-accounts`).  Prose is model
 * output and is never trusted as graph data on its own: a reference only
 * becomes navigable when it resolves against a node that actually exists in
 * the currently loaded graph.  Unresolved references stay inert so the UI
 * never offers an affordance that would lead nowhere.
 *
 * Forward compatibility: the backend currently returns an empty `results`
 * array, so prose is the only entity carrier.  When `results` is populated it
 * becomes the authoritative entity list and prose parsing degrades to
 * enrichment — see `resolveAnswerEntities`, where that is a single input
 * change rather than a rewrite.
 */

import type { GraphSearchResultItem, NodeInstance } from '../types';

/** A prose reference that matched a real node in the loaded graph. */
export interface ResolvedEntity {
  /** The token as written in the answer. */
  token: string;
  /** The live graph node it resolved to. */
  node: NodeInstance;
}

export interface AnswerEntityIndex {
  /** Lookup from the literal prose token to its resolved node. */
  byToken: Map<string, NodeInstance>;
  /** Distinct resolved nodes, in order of first appearance in the answer. */
  entities: ResolvedEntity[];
  /** Tokens that looked like entity references but matched no live node. */
  unresolvedTokens: string[];
}

const EMPTY_INDEX: AnswerEntityIndex = {
  byToken: new Map(),
  entities: [],
  unresolvedTokens: [],
};

/**
 * Relationship types are written in SCREAMING_SNAKE_CASE (PARTICIPATES_IN,
 * REALIZES).  They are vocabulary, not node references, so they must never be
 * offered as navigable chips.
 */
const RELATIONSHIP_TOKEN = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/;

export function isRelationshipToken(token: string): boolean {
  return RELATIONSHIP_TOKEN.test(token.trim());
}

/**
 * Harvests inline-code spans, which is how the agent writes entity references.
 * Fenced blocks are stripped first so code samples are not mined for entities.
 */
export function extractInlineCodeTokens(markdown: string): string[] {
  const tokens: string[] = [];
  const withoutFences = markdown.replace(/```[\s\S]*?```/g, '');
  const pattern = /`([^`\n]+)`/g;
  let match = pattern.exec(withoutFences);
  while (match !== null) {
    tokens.push(match[1]);
    match = pattern.exec(withoutFences);
  }
  return tokens;
}

/** An answer split into its concise summary and the supporting detail. */
export interface SplitAnswer {
  /** The summary section body, without its heading. */
  summary: string;
  /** Everything preceding the summary heading. */
  detail: string;
}

/**
 * Headings the agent may use for its concluding summary.  The agent prompt asks
 * for "Summary", but the wording is model output and drifts, so near synonyms
 * are accepted rather than trusted to be exact.
 */
const SUMMARY_HEADING = /^[ \t]{0,3}(#{1,6})[ \t]+(summary|tl;?dr|in short|in summary|bottom line)[ \t:]*$/im;

/**
 * Splits an answer into its summary and the detail that precedes it.
 *
 * Returns `null` when no summary heading is present.  Callers must treat that
 * as "render the whole answer as-is": guessing at a summary would risk
 * promoting an arbitrary fragment as though it were the agent's conclusion.
 */
export function splitSummary(answer: string): SplitAnswer | null {
  // Ignore headings inside fenced blocks by blanking the fences first, keeping
  // offsets stable so indices still map onto the original string.
  const masked = answer.replace(/```[\s\S]*?```/g, (block) => ' '.repeat(block.length));

  const match = SUMMARY_HEADING.exec(masked);
  if (!match || match.index === undefined) return null;

  const summary = answer.slice(match.index + match[0].length).trim();
  const detail = answer.slice(0, match.index).trim();

  // A heading with nothing under it is not a summary worth promoting.
  if (summary === '') return null;

  return { summary, detail };
}

/**
 * Entities cited in the summary but never established in the detail.
 *
 * The summary is generated in the same pass as the body, so it cannot be
 * verified as accurate — but it can be checked for *consistency*.  An entity
 * that appears only in the summary was not grounded by the reasoning above it,
 * which is a signal worth showing a curator.
 *
 * Only backticked references are visible to this check, so a summary that names
 * entities in prose or bold will report no drift.
 */
export function findSummaryDrift(split: SplitAnswer): string[] {
  const detailTokens = new Set(
    extractInlineCodeTokens(split.detail).map((t) => t.trim().toLowerCase()),
  );

  const drifted: string[] = [];
  const seen = new Set<string>();
  for (const raw of extractInlineCodeTokens(split.summary)) {
    const token = raw.trim();
    const key = token.toLowerCase();
    if (token === '' || seen.has(key)) continue;
    seen.add(key);
    if (isRelationshipToken(token)) continue;
    if (!detailTokens.has(key)) drifted.push(token);
  }
  return drifted;
}

/**
 * Builds the lookup keys a node can be referenced by.  Ids are the primary
 * contract; names and aliases are accepted because the agent does not
 * consistently use ids in prose.
 */
function lookupKeysFor(node: NodeInstance): string[] {
  const keys = [node.id, node.name, ...(node.aliases ?? [])];
  return keys
    .filter((k): k is string => typeof k === 'string' && k.trim() !== '')
    .map((k) => k.trim().toLowerCase());
}

/**
 * Resolves entity references in an Ask Dewey answer against the loaded graph.
 *
 * @param tokens      Candidate reference tokens harvested from the answer
 *                    (the inline-code spans).
 * @param nodes       Nodes currently loaded in the graph — the grounding set.
 * @param resultItems Structured results from the backend, when available.
 *                    Treated as authoritative and merged ahead of prose.
 */
export function resolveAnswerEntities(
  tokens: readonly string[],
  nodes: readonly NodeInstance[] | undefined,
  resultItems: readonly GraphSearchResultItem[] = [],
): AnswerEntityIndex {
  if (!nodes || nodes.length === 0) {
    return EMPTY_INDEX;
  }

  const byKey = new Map<string, NodeInstance>();
  for (const node of nodes) {
    for (const key of lookupKeysFor(node)) {
      // First writer wins so an id is never shadowed by another node's alias.
      if (!byKey.has(key)) byKey.set(key, node);
    }
  }

  const byToken = new Map<string, NodeInstance>();
  const entities: ResolvedEntity[] = [];
  const seenNodeIds = new Set<string>();
  const unresolvedTokens: string[] = [];
  const seenTokens = new Set<string>();

  const admit = (token: string, node: NodeInstance): void => {
    byToken.set(token, node);
    if (!seenNodeIds.has(node.id)) {
      seenNodeIds.add(node.id);
      entities.push({ token, node });
    }
  };

  // Authoritative structured results first, so prose can never override them.
  for (const item of resultItems) {
    const node = byKey.get(item.node_id.trim().toLowerCase());
    if (node) admit(item.node_id, node);
  }

  for (const raw of tokens) {
    const token = raw.trim();
    if (token === '' || seenTokens.has(token)) continue;
    seenTokens.add(token);

    // Relationship vocabulary is styled elsewhere and is never a node chip.
    if (isRelationshipToken(token)) continue;

    const node = byKey.get(token.toLowerCase());
    if (node) {
      admit(token, node);
    } else {
      unresolvedTokens.push(token);
    }
  }

  return { byToken, entities, unresolvedTokens };
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function nodeForToken(index: AnswerEntityIndex, token: string): NodeInstance | undefined {
  const directMatch = index.byToken.get(token);
  if (directMatch) return directMatch;

  const normalized = token.toLowerCase();
  for (const [candidate, node] of index.byToken) {
    if (candidate.trim().toLowerCase() === normalized) return node;
  }
  return undefined;
}

/**
 * Removes a node's repeated display label immediately before its resolved
 * inline-code reference. The reference remains, allowing the renderer to
 * create one clickable chip instead of prose followed by the same chip.
 */
export function removeDuplicateEntityLabels(
  markdown: string,
  index: AnswerEntityIndex,
): string {
  const normalizeSegment = (segment: string): string => {
    let normalized = '';
    let cursor = 0;
    const tokenPattern = /`([^`\n]+)`/g;
    let tokenMatch = tokenPattern.exec(segment);

    while (tokenMatch !== null) {
      normalized += segment.slice(cursor, tokenMatch.index);
      const token = tokenMatch[1].trim();
      const node = token === '' || isRelationshipToken(token) ? undefined : nodeForToken(index, token);
      let consumedClosingParenthesis = false;

      if (node) {
        const labels = [node.name, ...(node.aliases ?? [])]
          .filter((label): label is string => typeof label === 'string' && label.trim() !== '')
          .sort((left, right) => right.length - left.length);

        for (const label of labels) {
          const duplicate = new RegExp(
            `(?:(?:\\*\\*|__)${escapeRegExp(label.trim())}(?:\\*\\*|__)|(?:\\*|_)${escapeRegExp(label.trim())}(?:\\*|_)|${escapeRegExp(label.trim())})[ \\t]*(\\([ \\t]*)?$`,
            'i',
          );
          const duplicateMatch = normalized.match(duplicate);
          if (!duplicateMatch) continue;

          normalized = normalized.slice(0, -duplicateMatch[0].length);
          consumedClosingParenthesis = duplicateMatch[1] !== undefined;
          break;
        }
      }

      normalized += tokenMatch[0];
      cursor = tokenMatch.index + tokenMatch[0].length;
      if (consumedClosingParenthesis && segment[cursor] === ')') cursor += 1;
      tokenMatch = tokenPattern.exec(segment);
    }

    return normalized + segment.slice(cursor);
  };

  // Fenced blocks are code samples, not answer prose, so preserve them exactly.
  return markdown
    .split(/(```[\s\S]*?```)/g)
    .map((segment, index) => (index % 2 === 0 ? normalizeSegment(segment) : segment))
    .join('');
}

/**
 * Highlight scores for the answer's entities.  The focused entity outranks the
 * rest so it stays visually dominant while its supporting cast remains visible.
 */
export function entityHighlightScores(
  entities: readonly ResolvedEntity[],
  focusNodeId?: string | null,
): Map<string, number> {
  const scores = new Map<string, number>();
  for (const { node } of entities) {
    scores.set(node.id, node.id === focusNodeId ? 1.0 : 0.55);
  }
  return scores;
}
